import { useState, useEffect, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, Marker, useMapEvents, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Trash2, Navigation, Anchor, Ship, RotateCcw, Flag, MapPin, Maximize2, Minimize2, X } from "lucide-react";

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom icons
const createIcon = (color, label) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:${color};width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:11px;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);">${label}</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const departureIcon = createIcon("#22C55E", "D");
const seaIcon = createIcon("#3B82F6", "");
const arrivalIcon = createIcon("#EF4444", "A");
const transitionIcon = (num) => createIcon("#F59E0B", num);

// Calculate distance between two points in nautical miles
const calcDist = (p1, p2) => {
  const R = 3440.065;
  const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
  const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(p1.lat*Math.PI/180) * Math.cos(p2.lat*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

// Map click handler
function MapClickHandler({ onClick, enabled }) {
  useMapEvents({ click: (e) => enabled && onClick(e.latlng) });
  return null;
}

export function RouteMap({ value, onChange, translations }) {
  const tr = translations || {
    title: "Tracer le trajet",
    instructions: "Cliquez sur la carte pour tracer le trajet complet du navire",
    departurePort: "Zone de départ (port)",
    seaRiver: "Zone de navigation (mer/rivière)",
    arrivalPort: "Zone d'arrivée (port)",
    speed: "Vitesse (nœuds)",
    distance: "Distance (NM)",
    duration: "Durée (min)",
    totalDistance: "Distance totale",
    totalDuration: "Durée totale estimée",
    startDrawing: "Tracer le trajet",
    stopDrawing: "Terminer",
    clearRoute: "Effacer",
    markDepartureEnd: "Marquer fin zone départ",
    markArrivalStart: "Marquer début zone arrivée",
    step1: "1. Tracez le trajet",
    step2: "2. Définissez les zones",
    clickToMark: "Cliquez sur un point du trajet",
  };

  const [mode, setMode] = useState("idle"); // idle, drawing, markDeparture, markArrival
  const [points, setPoints] = useState(value?.points || []);
  const [departureEndIdx, setDepartureEndIdx] = useState(value?.departureEndIdx ?? null);
  const [arrivalStartIdx, setArrivalStartIdx] = useState(value?.arrivalStartIdx ?? null);
  const [speeds, setSpeeds] = useState(value?.speeds || { departure: 5, sea: 12, arrival: 5 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Calculate distances for each zone
  const zoneDistances = useMemo(() => {
    if (points.length < 2) return { departure: 0, sea: 0, arrival: 0, total: 0 };
    
    let departure = 0, sea = 0, arrival = 0;
    const depEnd = departureEndIdx ?? 0;
    const arrStart = arrivalStartIdx ?? points.length - 1;
    
    for (let i = 1; i < points.length; i++) {
      const d = calcDist(points[i-1], points[i]);
      if (i <= depEnd) departure += d;
      else if (i > arrStart) arrival += d;
      else sea += d;
    }
    
    // If no zones marked, all is "sea"
    if (departureEndIdx === null && arrivalStartIdx === null) {
      const total = departure + sea + arrival;
      return { departure: 0, sea: Math.round(total * 10) / 10, arrival: 0, total: Math.round(total * 10) / 10 };
    }
    
    const total = departure + sea + arrival;
    return { 
      departure: Math.round(departure * 10) / 10, 
      sea: Math.round(sea * 10) / 10, 
      arrival: Math.round(arrival * 10) / 10,
      total: Math.round(total * 10) / 10
    };
  }, [points, departureEndIdx, arrivalStartIdx]);

  // Calculate durations
  const durations = useMemo(() => ({
    departure: speeds.departure > 0 ? Math.round((zoneDistances.departure / speeds.departure) * 60) : 0,
    sea: speeds.sea > 0 ? Math.round((zoneDistances.sea / speeds.sea) * 60) : 0,
    arrival: speeds.arrival > 0 ? Math.round((zoneDistances.arrival / speeds.arrival) * 60) : 0,
  }), [zoneDistances, speeds]);

  const totalDuration = durations.departure + durations.sea + durations.arrival;

  // Notify parent
  useEffect(() => {
    onChange({
      points,
      departureEndIdx,
      arrivalStartIdx,
      speeds,
      segments: {
        departure: { distance: zoneDistances.departure, speed: speeds.departure },
        sea: { distance: zoneDistances.sea, speed: speeds.sea },
        arrival: { distance: zoneDistances.arrival, speed: speeds.arrival },
      },
      totalDistance: zoneDistances.total,
      totalDuration,
    });
  }, [points, departureEndIdx, arrivalStartIdx, speeds, zoneDistances, totalDuration, onChange]);

  const handleMapClick = useCallback((latlng) => {
    if (mode === "drawing") {
      setPoints(prev => [...prev, { lat: latlng.lat, lng: latlng.lng }]);
    }
  }, [mode]);

  const handlePointClick = (index) => {
    if (mode === "markDeparture") {
      setDepartureEndIdx(index);
      setMode("idle");
      // Ensure arrival is after departure
      if (arrivalStartIdx !== null && arrivalStartIdx <= index) {
        setArrivalStartIdx(null);
      }
    } else if (mode === "markArrival") {
      // Ensure arrival is after departure
      if (departureEndIdx === null || index > departureEndIdx) {
        setArrivalStartIdx(index);
      }
      setMode("idle");
    }
  };

  const handleClear = () => {
    setPoints([]);
    setDepartureEndIdx(null);
    setArrivalStartIdx(null);
    setMode("idle");
  };

  // Build colored polyline segments
  const polylineSegments = useMemo(() => {
    if (points.length < 2) return [];
    
    const segments = [];
    const depEnd = departureEndIdx ?? -1;
    const arrStart = arrivalStartIdx ?? points.length;
    
    for (let i = 1; i < points.length; i++) {
      let color = "#3B82F6"; // Blue for sea
      if (i <= depEnd) color = "#22C55E"; // Green for departure
      else if (i > arrStart) color = "#EF4444"; // Red for arrival
      
      segments.push({
        positions: [[points[i-1].lat, points[i-1].lng], [points[i].lat, points[i].lng]],
        color
      });
    }
    return segments;
  }, [points, departureEndIdx, arrivalStartIdx]);

  const canMarkZones = points.length >= 3;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-[#0B2A4A] flex items-center gap-2">
          <Navigation className="w-5 h-5" />
          {tr.title}
        </h3>
        <div className="flex gap-2 flex-wrap">
          {mode === "idle" && points.length === 0 && (
            <Button type="button" onClick={() => setMode("drawing")} className="bg-[#0B2A4A]" data-testid="start-drawing-btn">
              <MapPin className="w-4 h-4 mr-1" /> {tr.startDrawing}
            </Button>
          )}
          {mode === "drawing" && (
            <Button type="button" onClick={() => setMode("idle")} className="bg-green-600 hover:bg-green-700" data-testid="stop-drawing-btn">
              {tr.stopDrawing}
            </Button>
          )}
          {points.length > 0 && mode === "idle" && (
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setMode("drawing")} data-testid="continue-drawing-btn">
                + Ajouter points
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleClear} data-testid="clear-route-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> {tr.clearRoute}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-2">
        <div className={`flex items-center gap-2 ${points.length >= 2 ? 'text-green-600' : 'text-gray-600'}`}>
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${points.length >= 2 ? 'bg-green-600 text-white' : 'bg-gray-300'}`}>1</span>
          {tr.step1} {points.length >= 2 && `✓ (${points.length} points)`}
        </div>
        <div className={`flex items-center gap-2 ${departureEndIdx !== null || arrivalStartIdx !== null ? 'text-green-600' : 'text-gray-600'}`}>
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${departureEndIdx !== null || arrivalStartIdx !== null ? 'bg-green-600 text-white' : 'bg-gray-300'}`}>2</span>
          {tr.step2}
        </div>
      </div>

      {/* Map */}
      <div className={`${isFullscreen ? 'fixed inset-0 z-[9999] bg-white' : 'relative'} rounded-lg overflow-hidden border-2 border-gray-200`} style={isFullscreen ? {} : { height: "350px" }}>
        {/* Fullscreen toggle button */}
        <button
          type="button"
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="absolute top-2 right-2 z-[1000] bg-white p-2 rounded-lg shadow-md hover:bg-gray-100 transition-colors"
          data-testid="fullscreen-toggle-btn"
        >
          {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
        </button>

        {/* Close button in fullscreen */}
        {isFullscreen && (
          <button
            type="button"
            onClick={() => setIsFullscreen(false)}
            className="absolute top-2 right-14 z-[1000] bg-red-500 text-white p-2 rounded-lg shadow-md hover:bg-red-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Fullscreen controls */}
        {isFullscreen && (
          <div className="absolute top-2 left-2 z-[1000] flex gap-2">
            {mode === "idle" && points.length === 0 && (
              <Button type="button" onClick={() => setMode("drawing")} className="bg-[#0B2A4A] shadow-lg" data-testid="fs-start-drawing-btn">
                <MapPin className="w-4 h-4 mr-1" /> {tr.startDrawing}
              </Button>
            )}
            {mode === "drawing" && (
              <Button type="button" onClick={() => setMode("idle")} className="bg-green-600 hover:bg-green-700 shadow-lg">
                {tr.stopDrawing}
              </Button>
            )}
            {points.length > 0 && mode === "idle" && (
              <>
                <Button type="button" variant="secondary" size="sm" onClick={() => setMode("drawing")} className="shadow-lg">
                  + Ajouter points
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={handleClear} className="shadow-lg">
                  <RotateCcw className="w-4 h-4 mr-1" /> {tr.clearRoute}
                </Button>
                {canMarkZones && (
                  <>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setMode("markDeparture")} className={`shadow-lg ${departureEndIdx !== null ? 'bg-green-100' : ''}`}>
                      <Flag className="w-4 h-4 mr-1 text-green-600" /> Fin départ
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setMode("markArrival")} className={`shadow-lg ${arrivalStartIdx !== null ? 'bg-red-100' : ''}`}>
                      <Flag className="w-4 h-4 mr-1 text-red-600" /> Début arrivée
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Fullscreen info panel */}
        {isFullscreen && points.length >= 2 && (
          <div className="absolute bottom-4 left-4 right-4 z-[1000] bg-white/95 backdrop-blur rounded-lg shadow-xl p-4">
            <div className="flex justify-between items-center gap-4 flex-wrap">
              <div className="flex gap-4 items-center">
                <div className="text-center">
                  <div className="text-xs text-green-600 font-medium">Départ</div>
                  <div className="font-bold">{zoneDistances.departure} NM</div>
                  <Input type="number" min="0" step="0.5" value={speeds.departure} onChange={(e) => setSpeeds({...speeds, departure: parseFloat(e.target.value) || 0})} className="h-7 w-20 text-center mt-1" />
                  <div className="text-xs text-gray-500">{durations.departure} min</div>
                </div>
                <div className="text-2xl text-gray-300">+</div>
                <div className="text-center">
                  <div className="text-xs text-blue-600 font-medium">Navigation</div>
                  <div className="font-bold">{zoneDistances.sea} NM</div>
                  <Input type="number" min="0" step="0.5" value={speeds.sea} onChange={(e) => setSpeeds({...speeds, sea: parseFloat(e.target.value) || 0})} className="h-7 w-20 text-center mt-1" />
                  <div className="text-xs text-gray-500">{durations.sea} min</div>
                </div>
                <div className="text-2xl text-gray-300">+</div>
                <div className="text-center">
                  <div className="text-xs text-red-600 font-medium">Arrivée</div>
                  <div className="font-bold">{zoneDistances.arrival} NM</div>
                  <Input type="number" min="0" step="0.5" value={speeds.arrival} onChange={(e) => setSpeeds({...speeds, arrival: parseFloat(e.target.value) || 0})} className="h-7 w-20 text-center mt-1" />
                  <div className="text-xs text-gray-500">{durations.arrival} min</div>
                </div>
              </div>
              <div className="text-center bg-[#0B2A4A] text-white px-6 py-3 rounded-lg">
                <div className="text-xs opacity-80">Total</div>
                <div className="text-xl font-bold">{zoneDistances.total} NM • {totalDuration} min</div>
              </div>
            </div>
          </div>
        )}

        <MapContainer center={[46.5, -1.5]} zoom={6} style={{ height: isFullscreen ? "100vh" : "100%", width: "100%" }} className="z-0">
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapClickHandler onClick={handleMapClick} enabled={mode === "drawing"} />

          {/* Colored polylines */}
          {polylineSegments.map((seg, i) => (
            <Polyline key={i} positions={seg.positions} color={seg.color} weight={5} opacity={0.9} />
          ))}

          {/* Markers */}
          {points.map((point, index) => {
            let icon = seaIcon;
            let label = `Point ${index + 1}`;
            
            if (index === 0) { icon = departureIcon; label = "Départ"; }
            else if (index === points.length - 1 && points.length > 1) { icon = arrivalIcon; label = "Arrivée"; }
            else if (index === departureEndIdx) { icon = transitionIcon("→"); label = "Fin zone départ"; }
            else if (index === arrivalStartIdx) { icon = transitionIcon("←"); label = "Début zone arrivée"; }

            const isClickable = (mode === "markDeparture" || mode === "markArrival") && index > 0 && index < points.length - 1;

            return (
              <Marker 
                key={index} 
                position={[point.lat, point.lng]} 
                icon={icon}
                draggable={mode === "idle"}
                eventHandlers={{ 
                  click: () => isClickable && handlePointClick(index),
                  dragend: (e) => {
                    const newPos = e.target.getLatLng();
                    setPoints(prev => prev.map((p, i) => i === index ? { lat: newPos.lat, lng: newPos.lng } : p));
                  }
                }}
              >
                <Popup>
                  <div className="text-center">
                    <strong>{label}</strong>
                    <div className="text-xs text-gray-500 mt-1">Glissez pour déplacer</div>
                    {(mode === "markDeparture" || mode === "markArrival") && index > 0 && index < points.length - 1 && (
                      <div className="mt-2">
                        <button onClick={() => handlePointClick(index)} className="text-blue-600 text-xs font-medium hover:underline">
                          {mode === "markDeparture" ? "Marquer fin départ" : "Marquer début arrivée"}
                        </button>
                      </div>
                    )}
                    <button 
                      onClick={() => setPoints(prev => prev.filter((_, i) => i !== index))}
                      className="mt-2 text-red-500 text-xs hover:underline block w-full"
                    >
                      Supprimer ce point
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Mode indicators */}
        {mode === "drawing" && (
          <div className="absolute top-2 left-2 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium z-[1000] flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            Cliquez pour ajouter des points
          </div>
        )}
        {(mode === "markDeparture" || mode === "markArrival") && (
          <div className="absolute top-2 left-2 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-medium z-[1000]">
            {tr.clickToMark}
          </div>
        )}
      </div>

      {/* Zone marking buttons */}
      {canMarkZones && mode === "idle" && (
        <div className="flex gap-2 justify-center">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => setMode("markDeparture")}
            className={`border-green-500 text-green-700 hover:bg-green-50 ${departureEndIdx !== null ? 'bg-green-50' : ''}`}
            data-testid="mark-departure-btn"
          >
            <Flag className="w-4 h-4 mr-1" /> 
            {tr.markDepartureEnd} {departureEndIdx !== null && `(P${departureEndIdx + 1})`}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => setMode("markArrival")}
            className={`border-red-500 text-red-700 hover:bg-red-50 ${arrivalStartIdx !== null ? 'bg-red-50' : ''}`}
            data-testid="mark-arrival-btn"
          >
            <Flag className="w-4 h-4 mr-1" /> 
            {tr.markArrivalStart} {arrivalStartIdx !== null && `(P${arrivalStartIdx + 1})`}
          </Button>
        </div>
      )}

      {/* Zone cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-green-700 font-semibold">
              <Anchor className="w-4 h-4" /> {tr.departurePort}
            </div>
            <div className="text-2xl font-bold text-green-600">{zoneDistances.departure} NM</div>
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-gray-500">{tr.speed}</Label>
                <Input type="number" min="0" step="0.5" value={speeds.departure} onChange={(e) => setSpeeds({...speeds, departure: parseFloat(e.target.value) || 0})} className="h-8" data-testid="departure-speed-input" />
              </div>
              <div className="text-sm text-gray-600">{tr.duration}: <strong>{durations.departure} min</strong></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-blue-700 font-semibold">
              <Ship className="w-4 h-4" /> {tr.seaRiver}
            </div>
            <div className="text-2xl font-bold text-blue-600">{zoneDistances.sea} NM</div>
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-gray-500">{tr.speed}</Label>
                <Input type="number" min="0" step="0.5" value={speeds.sea} onChange={(e) => setSpeeds({...speeds, sea: parseFloat(e.target.value) || 0})} className="h-8" data-testid="sea-speed-input" />
              </div>
              <div className="text-sm text-gray-600">{tr.duration}: <strong>{durations.sea} min</strong></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-red-700 font-semibold">
              <Anchor className="w-4 h-4" /> {tr.arrivalPort}
            </div>
            <div className="text-2xl font-bold text-red-600">{zoneDistances.arrival} NM</div>
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-gray-500">{tr.speed}</Label>
                <Input type="number" min="0" step="0.5" value={speeds.arrival} onChange={(e) => setSpeeds({...speeds, arrival: parseFloat(e.target.value) || 0})} className="h-8" data-testid="arrival-speed-input" />
              </div>
              <div className="text-sm text-gray-600">{tr.duration}: <strong>{durations.arrival} min</strong></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total */}
      <div className="bg-[#0B2A4A] text-white p-4 rounded-lg flex justify-between items-center">
        <div>
          <span className="text-sm opacity-80">{tr.totalDistance}</span>
          <p className="text-2xl font-bold">{zoneDistances.total} NM</p>
        </div>
        <div className="text-center">
          <div className="flex gap-1 text-xs opacity-80">
            <span className="px-2 py-0.5 bg-green-500 rounded">{zoneDistances.departure}</span>
            <span>+</span>
            <span className="px-2 py-0.5 bg-blue-500 rounded">{zoneDistances.sea}</span>
            <span>+</span>
            <span className="px-2 py-0.5 bg-red-500 rounded">{zoneDistances.arrival}</span>
          </div>
        </div>
        <div className="text-right">
          <span className="text-sm opacity-80">{tr.totalDuration}</span>
          <p className="text-2xl font-bold">{totalDuration} min</p>
        </div>
      </div>
    </div>
  );
}
